import { Icon, LucideIcon } from "@ews-admin/global-design-system";
import React from "react";
import { Link } from "react-router-dom";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  change?: {
    value: string | number;
    type: "increase" | "decrease" | "neutral";
  };
  description?: string;
  className?: string;
  // When set, the whole card is a link to this route (keyboard accessible).
  // Without it the card is static: no pointer cursor, no hover lift.
  to?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  change,
  description,
  className = "",
  to,
}) => {
  const getChangeColor = (type: "increase" | "decrease" | "neutral") => {
    switch (type) {
      case "increase":
        return "text-success-600 bg-success-50";
      case "decrease":
        return "text-danger-600 bg-danger-50";
      case "neutral":
        return "text-neutral-600 bg-neutral-50";
      default:
        return "text-neutral-600 bg-neutral-50";
    }
  };

  const getChangeIcon = (type: "increase" | "decrease" | "neutral") => {
    switch (type) {
      case "increase":
        return "↗";
      case "decrease":
        return "↘";
      case "neutral":
        return "→";
      default:
        return "→";
    }
  };

  const content = (
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <p className="mb-1 text-xs font-medium text-neutral-600">{title}</p>
          <div className="flex gap-2 items-center">
            <p className="mb-1 text-2xl font-bold text-neutral-900">{value}</p>

            {change && (
              <div
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105 ${getChangeColor(
                  change.type
                )}`}
              >
                <span className="mr-1">{getChangeIcon(change.type)}</span>
                {change.value}
              </div>
            )}
          </div>

          {description && (
            <p className="mt-1 text-xs text-neutral-500">{description}</p>
          )}
        </div>

        <div className="flex-shrink-0">
          <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-ews-primary hover-scale">
            <Icon icon={icon} size={"md"} color="white" />
          </div>
        </div>
      </div>
  );

  const baseClasses = `block p-4 bg-white rounded-xl border shadow-soft border-neutral-200 ${className}`;

  if (to) {
    return (
      <Link
        to={to}
        className={`${baseClasses} cursor-pointer transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
      >
        {content}
      </Link>
    );
  }

  return <div className={baseClasses}>{content}</div>;
};
